import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SquareLoader } from "react-spinners";
import Header from "../../../../components/Header";
import axiosWithAuth from "../../../../utils/axiosWithAuth";
import { getApiEndpoint } from "../../../../utils/apiConfig";
import "./_MyHomeDetails.scss";
import RoomTypeImg from "../../../../assets/images/RoomTypeDetails.png";
import { getPropertyById } from "../../../../services/propertyService";
import { getRepairsByPropertyId } from "../../../../services/repairService";
import DirectBreakdownSave from "../../../../DirectSave";
import { calculateRepairBreakdown } from "../../../../services/calculationService";
import { getStylesForCalculation } from "../../../../services/stylesService";
import { getTariffs } from "../../../../services/tariffsService";
import { saveBreakdownDirectly } from "../../../../utils/breakdownUtils";
import { getBreakdownDetails } from "../../../../services/breakdownService";
import { toast } from "react-toastify";
import Loader from '../../../../components/Loader/Loader';
import PropertyEditModal from '../../../../components/PropertyEditModal/PropertyEditModal';

// Icon imports
import { ReactComponent as RoomParametrIcon } from "../../../../assets/icons/RoomParametr.svg";
import { ReactComponent as RequiredMaterialIcon } from "../../../../assets/icons/RequiredMaterial.svg";
import { ReactComponent as StatisticsIcon } from "../../../../assets/icons/Statistics.svg";
import { ReactComponent as DesignStyleIcon } from "../../../../assets/icons/DesignStyle.svg";
import { ReactComponent as RepairDateIcon } from "../../../../assets/icons/RepairDate.svg";
import { ReactComponent as RepairStatusIcon } from "../../../../assets/icons/RepairStatus.svg";
import { ReactComponent as MyHomeIcon } from "../../../../assets/icons/MyHome.svg";

const MyHomeDetails = () => {
  const { t } = useTranslation();
  const [property, setProperty] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [selectedTariff, setSelectedTariff] = useState(null);
  const [styles, setStyles] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [calculationLoading, setCalculationLoading] = useState(false);
  const [breakdownData, setBreakdownData] = useState(null);
  const [savedBreakdownId, setSavedBreakdownId] = useState(null);
  const [isBreakdownMode, setIsBreakdownMode] = useState(false);
  const [showPropertyEditModal, setShowPropertyEditModal] = useState(false);
  
  const headerTitle = t("myHomeDetails.0");
  document.title = headerTitle;

  // Handle property save from edit modal
  const handlePropertySave = async (updatedProperty) => {
    try {
      // Update local property state
      setProperty({...property, ...updatedProperty});
      setShowPropertyEditModal(false);
      toast.success("Property updated successfully!");
    } catch (error) {
      console.error("Error updating property:", error);
      toast.error(error.message || "Failed to update property");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // URL path'indeki id'nin breakdown ID mi yoksa property ID mi olduğunu kontrol et
        const pathParts = window.location.pathname.split('/');
        const isBreakdownPath = pathParts.includes('breakdown');
        setIsBreakdownMode(isBreakdownPath);
        
        // Direk breakdown data kontrolü - state üzerinden geçtiyse
        const directBreakdownData = location.state?.breakdownData;
        
        // Veri kaynağına göre farklı veri çek
        if (isBreakdownPath) {
          // Eğer navigate state'ten direk breakdown verileri alındıysa, API çağrısı yapmayız
          if (directBreakdownData) {
            console.log("Using direct breakdown data from navigation state");
            setBreakdown(directBreakdownData);
            setBreakdownData(directBreakdownData);
            
            // Style ve tariff bilgilerini kaydet
            if (directBreakdownData.repairStyle && directBreakdownData.repairStyle._id) {
              setSelectedStyle(directBreakdownData.repairStyle._id);
            }
            
            if (directBreakdownData.repairTariff && directBreakdownData.repairTariff._id) {
              setSelectedTariff(directBreakdownData.repairTariff._id);
        }
        
            // Sanal property oluştur
            const virtualProperty = createVirtualPropertyFromBreakdown(directBreakdownData);
            setProperty(virtualProperty);
          } else {
            // API'den breakdown details çek
            console.log("Fetching breakdown with ID:", id);
            const breakdownResponse = await getBreakdownDetails(id);
            setBreakdown(breakdownResponse);
            setBreakdownData(breakdownResponse);
            
            // Style ve tariff bilgilerini kaydet
            if (breakdownResponse.repairStyle && breakdownResponse.repairStyle._id) {
              setSelectedStyle(breakdownResponse.repairStyle._id);
            }
            
            if (breakdownResponse.repairTariff && breakdownResponse.repairTariff._id) {
              setSelectedTariff(breakdownResponse.repairTariff._id);
            }
            
            // Sanal property oluştur
            const virtualProperty = createVirtualPropertyFromBreakdown(breakdownResponse);
            setProperty(virtualProperty);
          }
        } else {
          // Normal property details çek
          console.log("Fetching property with ID:", id);
          const propertyResponse = await getPropertyById(id);
          setProperty(propertyResponse.data);
          
          // Fetch repairs for this property
          const repairsResponse = await getRepairsByPropertyId(id);
          setRepairs(repairsResponse.data);
          }
        
        // Fetch styles and tariffs for calculation
        const [stylesData, tariffsData] = await Promise.all([
          getStylesForCalculation(),
          getTariffs()
        ]);
        
        setStyles(stylesData.data || []);
        setTariffs(tariffsData.data || []);
        
        // Set default style and tariff if not already set
        if (!selectedStyle && stylesData.data?.length > 0) {
          setSelectedStyle(stylesData.data[0].id);
        }
        
        if (!selectedTariff && tariffsData.data?.length > 0) {
          setSelectedTariff(tariffsData.data[0].id);
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching details:", error);
        setError(error.message || "Failed to load details");
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id, location.state]);
  
  // Breakdown'dan sanal property oluştur
  const createVirtualPropertyFromBreakdown = (breakdown) => {
    // Breakdown'daki odaların toplam alanını hesapla
    const totalArea = breakdown.breakdown?.rooms?.reduce((total, room) => {
      return total + (parseFloat(room.area) || 0);
    }, 0) || 0;
    
    // Style ve tariff adlarını al
    const styleName = breakdown.repairStyle?.name?.text || breakdown.repairStyle?.name?.az || t("general.unknown");
    const tariffName = breakdown.repairTariff?.name?.text || breakdown.repairTariff?.name?.az || t("general.unknown");
    
    return {
      id: `breakdown-${breakdown._id}`,
      _id: breakdown._id,
      breakdownId: breakdown._id,
      name: `${styleName} - ${tariffName}`,
      isVirtual: true,
      roomCount: breakdown.breakdown?.rooms?.length || 0,
      doorCount: breakdown.breakdown?.fixtures?.doors?.count || 0,
      windowCount: breakdown.breakdown?.fixtures?.windows?.count || 0,
      area: totalArea,
      propertyType: {
        name: { text: styleName },
        iconType: "fa-solid fa-file-invoice-dollar"
      },
      propertyStatus: {
        name: { text: tariffName }
      },
      costs: breakdown.costs,
      createdAt: breakdown.createdAt,
      rooms: breakdown.breakdown?.rooms || []
    };
  };

  const handlePropertyClick = (path) => {
    navigate(`/account/homeplan/${path}`);
  };
  
  const handleCalculateAndSave = async () => {
    try {
      if (!selectedStyle || !selectedTariff) {
        toast.error("Please select a style and tariff first");
        return;
      }
      
      setCalculationLoading(true);
      
      // Prepare room data from property
      const roomsData = property.rooms?.map(room => ({
        roomType: room.roomType?._id || room.roomType,
        area: room.area || 0
      })) || [];
      
      // Calculate repair breakdown
      const calculationResult = await calculateRepairBreakdown({
        styleId: selectedStyle,
        tariffId: selectedTariff,
        roomsData,
        doorCount: property.doorCount || 0,
        windowCount: property.windowCount || 0
      });
      
      setBreakdownData(calculationResult);
      
      // Property ID veya breakdown ID kullan
      const propertyId = isBreakdownMode ? property.breakdownId : id;
      
      // Save breakdown directly
      const savedData = await saveBreakdownDirectly(
        calculationResult, 
        {
          propertyId: propertyId,
          styleId: selectedStyle,
          tariffId: selectedTariff
        }
      );
      
      // Set saved ID and show success message
      setSavedBreakdownId(savedData.data._id);
      toast.success("Breakdown calculated and saved successfully!");
      
      // Sadece normal property modunda repairs'i yenileyelim
      if (!isBreakdownMode) {
        const repairsResponse = await getRepairsByPropertyId(id);
        setRepairs(repairsResponse.data);
      }
      
      setCalculationLoading(false);
    } catch (error) {
      console.error("Error calculating and saving breakdown:", error);
      toast.error(error.message || "Failed to calculate and save breakdown");
      setCalculationLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="property-details-loader">
        <SquareLoader
          color="#cda742"
          size={80}
          speedMultiplier={1}
        />
      </div>
    );
  }

  if (error || (!property && !breakdown)) {
    return (
      <div className="property-details-page">
        <Header title={headerTitle} showBackIcon={true} showProfileIcon={true} />
        <div className="alert alert-danger mt-3 mx-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error || t("general.errorLoading")}
        </div>
      </div>
    );
  }

  // Veri kaynağına göre başlıkları belirle
  const propertyTypeName = isBreakdownMode ? 
    property.propertyType?.name?.text : 
    (property.propertyType?.name?.text || t("general.unknown"));
    
  const propertyStatusName = isBreakdownMode ? 
    property.propertyStatus?.name?.text : 
    (property.propertyStatus?.name?.text || "");
  
  // Onarım var mı kontrol edelim
  const hasRepairs = repairs && repairs.length > 0;
  
  return (
    <div className="my-home-details-page">
      <div className="container">
        <div className="row">
          <div className="page-heading-container">
            <h1 className="page-heading">{property?.title || 'Property Details'}</h1>
            <Link to="/my-account/my-home" className="back-button">
              <i className="fa-solid fa-arrow-left"></i> Back to My Home
            </Link>
          </div>
        </div>
        
        <div className="row">
          <div className="col-md-12">
            <div className="property-detail-content">
              {loading ? (
                <Loader />
              ) : (
                <>
                  {/* Property Information Section */}
                  <div className="info-section">
                    <h2 className="section-title">Property Information</h2>
                    <div className="menu-cards-grid">
                      <div className="menu-card">
                        <div className="card-icon">
                          <i className="fas fa-ruler-combined"></i>
              </div>
                        <div className="card-content">
                          <h3>Area</h3>
                          <p>{property.area} m²</p>
              </div>
              </div>
                      <div className="menu-card">
                        <div className="card-icon">
                          <i className="fas fa-map-marker-alt"></i>
              </div>
                        <div className="card-content">
                          <h3>Address</h3>
                          <p>{property.address || "No address provided"}</p>
          </div>
                      </div>
                      <div className="menu-card">
                        <div className="card-icon">
                          <i className="fas fa-building"></i>
                        </div>
                        <div className="card-content">
                          <h3>Type</h3>
                          <p>{property.propertyType || "Not specified"}</p>
        </div>
      </div>
                      <div className="menu-card" onClick={() => setShowPropertyEditModal(true)}>
            <div className="card-icon">
                          <i className="fas fa-edit"></i>
            </div>
            <div className="card-content">
                          <h3>Edit Details</h3>
                          <p>Update property information</p>
                        </div>
            </div>
            </div>
          </div>
          
                  {/* Repair Information Section */}
                  <div className="info-section">
                    <h2 className="section-title">Repair Information</h2>
                    <div className="menu-cards-grid">
                      <div className="menu-card">
            <div className="card-icon">
                          <i className="fas fa-palette"></i>
            </div>
            <div className="card-content">
                          <h3>Style</h3>
                          <select
                            className="form-select"
                            value={selectedStyle}
                            onChange={(e) => setSelectedStyle(e.target.value)}
                          >
                            <option value="">Select a style</option>
                            {styles.map((style) => (
                              <option key={style._id} value={style._id}>
                                {style.name}
                              </option>
                            ))}
                          </select>
            </div>
            </div>
          
          <div className="menu-card">
            <div className="card-icon">
                          <i className="fa-solid fa-dollar-sign"></i>
            </div>
            <div className="card-content">
                          <h3>Tariff</h3>
                          <div className="tariff-selector">
                            <select
                              value={selectedTariff}
                              onChange={(e) => setSelectedTariff(e.target.value)}
                              className="form-select"
                              disabled={!selectedStyle}
                            >
                              <option value="">Select Tariff</option>
                              {tariffs.map((tariff) => (
                                <option key={tariff._id} value={tariff._id}>
                                  {tariff.name}
                                </option>
                              ))}
                            </select>
                          </div>
            </div>
            </div>
          </div>
        </div>
        
                  {/* Breakdown Summary Section */}
                  {breakdownData && (
                    <section className="info-section">
                      <h2 className="section-title">Cost Summary</h2>
                      <div className="cost-summary-card">
                        <div className="cost-items">
                          <div className="summary-item">
                            <span className="item-label">Materials:</span>
                            <span className="item-value">${breakdownData.materialsCost}</span>
                          </div>
                          <div className="summary-item">
                            <span className="item-label">Labor:</span>
                            <span className="item-value">${breakdownData.laborCost}</span>
            </div>
                          <div className="summary-item">
                            <span className="item-label">Additional:</span>
                            <span className="item-value">${breakdownData.additionalCost}</span>
            </div>
                          <div className="summary-item total">
                            <span className="item-label">Total:</span>
                            <span className="item-value">${breakdownData.totalCost}</span>
            </div>
          </div>
          
                        <div className="duration-info">
                          <i className="fa-regular fa-clock"></i>
                          <span>&nbsp; Estimated duration: {breakdownData.duration} days</span>
                        </div>
                        
                        {savedBreakdownId && (
                          <div className="saved-breakdown-message">
                            <i className="fa-solid fa-check-circle"></i>
                            Breakdown saved successfully!
            </div>
                        )}
                        
                        {savedBreakdownId && (
                          <Link to={`/my-account/breakdown/${savedBreakdownId}`} className="view-details-button">
                            <i className="fa-solid fa-eye"></i>
                            View Breakdown Details
                          </Link>
                        )}
            </div>
                    </section>
                  )}

                  {/* Calculate Section */}
                  <section className="info-section">
                    <h2 className="section-title">Calculate</h2>
                    <button 
                      className={`calculate-button ${calculationLoading ? 'loading' : ''}`}
                      onClick={handleCalculateAndSave}
                      disabled={calculationLoading || !selectedStyle || !selectedTariff}
                    >
                      {calculationLoading ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin"></i>
                          Calculating...
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-calculator"></i>
                          Calculate & Save
                        </>
                      )}
                    </button>
                    
                    {savedBreakdownId && !breakdownData && (
                      <div className="saved-breakdown-message">
                        <i className="fa-solid fa-check-circle me-2"></i>
                        {t("general.breakdownSaved")}
            </div>
                    )}
                    
                    {breakdownData && (
                      <div className="calculation-result-card">
                        <h3 className="calculation-title">Calculation Results</h3>
                        <div className="cost-items">
                          <div className="summary-item">
                            <span className="item-label">Style:</span>
                            <span className="item-value">{breakdownData.style?.name || 'N/A'}</span>
                          </div>
                          <div className="summary-item">
                            <span className="item-label">Tariff:</span>
                            <span className="item-value">{breakdownData.tariff?.name || 'N/A'}</span>
            </div>
                          <div className="summary-item">
                            <span className="item-label">Property Area:</span>
                            <span className="item-value">{property?.area} m²</span>
            </div>
            </div>
          </div>
                    )}
                  </section>

                  {/* Property Edit Modal */}
                  {showPropertyEditModal && (
                    <PropertyEditModal
                      property={property}
                      onClose={() => setShowPropertyEditModal(false)}
                      onSave={handlePropertySave}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyHomeDetails;
